import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../../context/I18nContext';
import { Button } from '../ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiGlobe, FiMenu, FiX, FiUser } = FiIcons;

export const Navbar = () => {
  const { t, locale, setLocale } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);

  const isDashboard = location.pathname.includes('/dashboard');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      subscription.unsubscribe();
    };
  }, []);

  if (isDashboard) return null;

  const isHome = location.pathname === '/';
  const textColor = !scrolled && isHome ? 'text-white' : 'text-zinc-900';
  const bgColor = !scrolled && isHome ? 'bg-transparent' : 'bg-white/80 backdrop-blur-xl border-b border-zinc-200';

  return (
    <header className={`fixed top-0 z-50 w-full transition-all duration-300 ${scrolled ? 'py-3' : 'py-5'} ${bgColor}`}>
      <div className="container mx-auto px-6 flex items-center justify-between">
        <div className="flex items-center gap-12">
          <Link to="/" className={`text-2xl font-bold tracking-tighter flex items-center gap-2 ${textColor}`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${!scrolled && isHome ? 'bg-white' : 'bg-zinc-900'}`}>
              <span className={`text-lg leading-none mt-[-2px] font-black ${!scrolled && isHome ? 'text-zinc-900' : 'text-white'}`}>f</span>
            </div>
            Foodrolla
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            <Link to="/marketplace" className={`text-sm font-semibold tracking-wide transition-colors ${!scrolled && isHome ? 'text-white/80 hover:text-white' : 'text-zinc-500 hover:text-zinc-900'}`}>
              {t('nav.marketplace')}
            </Link>
            <Link to="/suppliers" className={`text-sm font-semibold tracking-wide transition-colors ${!scrolled && isHome ? 'text-white/80 hover:text-white' : 'text-zinc-500 hover:text-zinc-900'}`}>
              {t('nav.suppliers')}
            </Link>
          </nav>
        </div>

        <div className="flex items-center gap-6">
          <div className="flex items-center bg-zinc-100/50 rounded-full p-1 border border-zinc-200/50">
            <button 
              onClick={() => setLocale('nl')} 
              className={`px-3 py-1 rounded-full text-[10px] font-black tracking-widest transition-all ${locale === 'nl' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-600'}`}
            >
              🌐 NL
            </button>
            <button 
              onClick={() => setLocale('en')} 
              className={`px-3 py-1 rounded-full text-[10px] font-black tracking-widest transition-all ${locale === 'en' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-600'}`}
            >
              🌐 EN
            </button>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button variant="secondary" className="rounded-full px-6 text-sm font-bold gap-2">
                  <SafeIcon icon={FiUser} /> Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className={`text-sm font-bold ${!scrolled && isHome ? 'text-white hover:bg-white/10' : 'text-zinc-600 hover:bg-zinc-100'}`}>
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button className={`rounded-full px-8 text-sm font-bold h-11 ${!scrolled && isHome ? 'bg-white text-black hover:bg-zinc-100' : 'bg-zinc-900 text-white'}`}>
                    {t('nav.signup')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className={`w-6 h-6 ${textColor}`} />
          </button>
        </div>
      </div>
    </header>
  );
};